import { useEffect, useState } from 'react';

import { InjectedFieldProps, PublicFieldProps, NumberField } from 'react-admin';
import {
  BumpResourceSheetDto,
  JobPhaseBumpApprovalStatus,
  RepairTypeEnum,
} from '@vatos-pas/common';
import { DateTime } from 'luxon';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/DialogContentText';
import { makeStyles } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { IconButton } from '@material-ui/core';

import { PermissionsProvider } from 'providers/permissionsProvider';
import { CAN_APPROVE_BUMPOUT } from 'providers/permissions';
import { Show } from 'components/Show';
import InfoDialog from 'components/InfoDialog';
import { isJobPhaseBumpType, isPendingSupervisorStatus } from '../utils';
import { BumpoutTimesheetFieldType } from '../views/bumpout-timesheet-list';
import TimeLogModal from './TimeLogModal';

type WorkedHoursFieldProps = {
  value: BumpoutTimesheetFieldType;
  loaded: boolean;
  onChangeField: (
    id: string,
    name: BumpoutTimesheetFieldType,
    value: DateTime | string | null,
  ) => void;
  handleRefetch: () => void;
} & PublicFieldProps &
  InjectedFieldProps<BumpResourceSheetDto>;

const WorkedHoursField = ({
  value,
  onChangeField,
  record,
  loaded,
  handleRefetch,
}: WorkedHoursFieldProps) => {
  const classes = useStyles();
  const { hasPermission } = PermissionsProvider.useContainer();

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const canApprove = hasPermission(CAN_APPROVE_BUMPOUT);
  const isJobPhaseBump = isJobPhaseBumpType(record?.bumpResourceType);

  useEffect(() => {
    if (!record?.id || !loaded) return;

    window.setTimeout(() => {
      onChangeField(
        record.id as string,
        value,
        record?.loggedHours != null ? String(record.loggedHours) : null,
      );
    }, 500);
  }, [record?.loggedHours]);

  if (!record?.id) return null;

  if (record.repairType === RepairTypeEnum.Piecework)
    return <Typography>-</Typography>;

  const hasLoggedTime = Boolean(record?.timeIn && record?.timeOut);
  const isPendingSupervisorAction =
    (record?.bumperApprovalStatus === JobPhaseBumpApprovalStatus.NOT_STARTED &&
      isJobPhaseBump) ||
    isPendingSupervisorStatus(record?.bumperApprovalStatus);

  let content: JSX.Element | null = null;

  // `Not equal` operator to check for both `undefined` or `null`
  if (record.approvedHours != null) {
    const originalHours =
      isJobPhaseBump && record.loggedHours === null
        ? 'Not Submitted'
        : record.loggedHours;

    content = (
      <Box display="flex" alignItems="center" gridColumnGap={4}>
        <NumberField source="approvedHours" />
        <Show condition={hasLoggedTime}>
          <IconButton size="small" onClick={() => setIsViewModalOpen(true)}>
            <AccessTimeIcon />
          </IconButton>
        </Show>
        <Show
          condition={
            (isJobPhaseBump && record.loggedHours === null) ||
            record.isLoggedHoursAdjusted
          }
        >
          <IconButton size="small" onClick={() => setIsInfoModalOpen(true)}>
            <InfoOutlinedIcon className={classes.infoWarning} />
          </IconButton>
        </Show>
        <InfoDialog
          open={isInfoModalOpen}
          handleClose={() => setIsInfoModalOpen(false)}
          title="Worked Hours"
          content={
            <>
              <Typography>Original: {originalHours}</Typography>
              <Typography>
                Supervisor Adjusted: {record.approvedHours}
              </Typography>
            </>
          }
        />
      </Box>
    );
  } else if (canApprove && isPendingSupervisorAction) {
    const displayValue =
      isJobPhaseBump && record.loggedHours === null
        ? 'Not Submitted'
        : record.loggedHours ?? '-';

    content = (
      <Box display="flex" alignItems="center" gridColumnGap={4}>
        <Typography
          className={
            record?.bumperApprovalStatus ===
            JobPhaseBumpApprovalStatus.PENDING_REVIEW
              ? classes.pendingReviewText
              : classes.text
          }
        >
          {displayValue}
        </Typography>
        <Show condition={hasLoggedTime}>
          <IconButton size="small" onClick={() => setIsViewModalOpen(true)}>
            <AccessTimeIcon fontSize="small" />
          </IconButton>
        </Show>
      </Box>
    );
  }

  if (!content) return null;

  return (
    <>
      {content}
      <Show condition={isViewModalOpen}>
        <TimeLogModal
          open={isViewModalOpen}
          handleClose={() => setIsViewModalOpen(false)}
          record={record}
          mode="view"
          onChangeField={onChangeField}
          handleRefetch={handleRefetch}
        />
      </Show>
    </>
  );
};

const useStyles = makeStyles({
  infoWarning: {
    color: '#ff9800',
  },
  text: {
    marginBottom: 0,
  },
  pendingReviewText: {
    color: 'red',
    fontWeight: 'bold',
    marginBottom: 0,
  },
});

export default WorkedHoursField;
